import { useCallback, useRef } from 'react';
import { cn } from '@divlab/divanui';

import * as ConfiguratorQuery from '@Queries/Configurator';
import useTranslation from '@Queries/useTranslation';
import useMedias from '@Hooks/useMedias';
import Link from '@UI/Link';
import BreadcrumbsWrapper from '@Components/BreadcrumbsWrapper';
import useScrollToRef from '../../../hooks/useScrollToRef';
import PhotoGallery from '../../PhotoGallery';
import ATRInfo from '../../ATRInfo';
import NewCollectionAnnounce from '../../NewCollectionAnnounce';
import Layout from '../../Layout';
import Sidebar from '../../Sidebar';
import styles from './Main.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { PageProductData } from '@Pages/PageProduct';
import type { PecomDeliveryParameters } from '@Components/PecomDelivery';

interface MainProps extends HTMLAttributes<HTMLDivElement> {
  page: PageProductData;
  goodsInfo: PecomDeliveryParameters[];
  trackingSidebarRef: (node?: Element | null) => void;
  onClickReviews: () => void;
  onClickCharacteristics: () => void;
}

const Main: FC<MainProps> = (props) => {
  const {
    page,
    goodsInfo,
    trackingSidebarRef,
    onClickReviews,
    onClickCharacteristics,
    className,
    ...restProps
  } = props;
  const { data: configurator } = ConfiguratorQuery.useConfigurator();
  const { isMobileM } = useMedias();
  const { t } = useTranslation();
  const { handleScrollTo } = useScrollToRef();
  const refAtr = useRef<HTMLDivElement>();

  const isAtr = page.isAtr;
  const newCollectionAnnounce = page.newCollectionAnnounce;
  const vozvyshayaComfort = page.vozvyshayaComfort;
  const flowersBlock = page.flowersBlock;
  const isModify = configurator.configuration.isCustom;

  const handleClickAtr = useCallback(() => {
    handleScrollTo(refAtr);
  }, [handleScrollTo]);

  return (
    <section
      {...restProps}
      className={cn(styles.main, className)}
      {...(page.product.isActive && {
        itemScope: true,
        itemType: 'http://schema.org/Product',
        itemRef: 'reviews-section',
      })}
    >
      <BreadcrumbsWrapper
        className={cn(styles.breadcrumbs, { [styles.outer]: true })}
        breadcrumbs={configurator.configuration.breadcrumbs}
        breadcrumbsSeoData={page.breadcrumbsSeoData}
      />
      <Layout
        className={styles.mainContent}
        cnSidebar={styles.sidebar}
        cnContent={styles.content}
        sidebar={
          !isMobileM && (
            <Sidebar
              visibilityTrackingRef={trackingSidebarRef}
              configurator={configurator}
              goodsInfo={goodsInfo}
              onClickReviews={onClickReviews}
              onClickAtr={handleClickAtr}
            />
          )
        }
      >
        <div>
          <BreadcrumbsWrapper
            className={cn(styles.breadcrumbs, { [styles.inner]: true })}
            breadcrumbs={configurator.configuration.breadcrumbs}
            breadcrumbsSeoData={page.breadcrumbsSeoData}
          />
          <div className={styles.gallery}>
            <PhotoGallery
              key={configurator.configuration.id}
              product={page.product}
              configuration={configurator.configuration}
              medias={page.gallery}
            />

            {isModify && (
              <div className={styles.variations}>
                <Link
                  className={styles.variationsHeading}
                  view='primary'
                  underlined
                  onClick={onClickCharacteristics}
                >
                  {t('selected-parameters')}
                </Link>
                <div className={styles.variationsText}>{t('variations-alert')}</div>
              </div>
            )}
          </div>
          {isMobileM && (
            <div className={styles.wrapperSidebar}>
              <Sidebar
                configurator={configurator}
                goodsInfo={goodsInfo}
                onClickReviews={onClickReviews}
                onClickAtr={handleClickAtr}
              />
            </div>
          )}
          {isAtr && (
            <div ref={refAtr}>
              <ATRInfo className={styles.promoInfo} />
            </div>
          )}
          {newCollectionAnnounce && (
            <div>
              <NewCollectionAnnounce className={styles.promoInfo} atrInfo={newCollectionAnnounce} />
            </div>
          )}
          {vozvyshayaComfort && (
            <div>
              <NewCollectionAnnounce className={styles.promoInfo} atrInfo={vozvyshayaComfort} />
            </div>
          )}
          {flowersBlock && (
            <div>
              <NewCollectionAnnounce className={styles.promoInfo} atrInfo={flowersBlock} />
            </div>
          )}
        </div>
      </Layout>
    </section>
  );
};

export default Main;
